import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import css from './LeadooVisualBot.module.css';

/**
 * LeadooVisualBot component
 * Safely embeds the Leadoo Visual Bot inline widget into the React DOM tree.
 * Handles client-side script injection, clean unmounting on SPA navigation,
 * and isolates Leadoo DOM changes from React reconciliation.
 *
 * @param {Object} props
 * @param {string} [props.code] - The Leadoo bot identifier code (default: '61f569dd')
 * @param {string} [props.className] - Custom class for the root section
 * @param {string} [props.rootClassName] - Overriding class for the root section
 * @returns {JSX.Element}
 */
const LeadooVisualBot = ({
  code = process.env.REACT_APP_LEADOO_BOT_CODE || '61f569dd',
  className = null,
  rootClassName = null,
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !code || typeof window === 'undefined') {
      return;
    }

    let refreshTimeout;
    const triggerRefresh = () => {
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(() => {
        import('gsap/ScrollTrigger')
          .then(({ default: ScrollTrigger }) => {
            ScrollTrigger.refresh();
          })
          .catch(() => {});
      }, 100);
    };

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      let lastHeight = 0;
      ro = new ResizeObserver(entries => {
        for (let entry of entries) {
          const height = entry?.contentRect?.height || 0;
          if (Math.abs(height - lastHeight) > 10) {
            lastHeight = height;
            triggerRefresh();
          }
        }
      });
      ro.observe(container);
    }

    // Clean any prior instances if re-mounting
    container.innerHTML = '';

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://bot.leadoo.com/bot/visual.js?code=${code}#seamless`;
    script.addEventListener('load', triggerRefresh, { once: true });

    container.appendChild(script);

    return () => {
      if (ro) {
        ro.disconnect();
      }
      clearTimeout(refreshTimeout);
      // Cleanup bot container and script on unmount
      if (container) {
        container.innerHTML = '';
      }
      triggerRefresh();
    };
  }, [code]);

  const classes = classNames(rootClassName || css.root, className);

  return (
    <section className={classes} aria-label="Interactive Assistant">
      <div className={css.mainContainer}>
        <div ref={containerRef} className={css.botContainer} />
      </div>
    </section>
  );
};

LeadooVisualBot.propTypes = {
  code: PropTypes.string,
  className: PropTypes.string,
  rootClassName: PropTypes.string,
};

export default React.memo(LeadooVisualBot);
